import { Button, Kind, Size } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { Toggle } from '@thanx/uikit/toggle'
import type { ServiceCharge } from 'api/serviceCharges'
import { isOverrideLocked, isRecommendedForDelivery } from 'api/serviceCharges'
import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { formatServiceChargeAmount } from '../../serviceChargeFormat'
import { tableBodyCellClass, tableRowHoverClass } from '../../tableStyles'
import Pill from './Pill'

const t = buildTranslate(
  'locations.location_list.location_detail.service_charges'
)

export const describeApplication = (
  charge: Pick<ServiceCharge, 'provider_criteria' | 'criteria_override'>
): { key: 'both' | 'delivery' | 'pickup' | 'none'; locked: boolean } => {
  const locked = isOverrideLocked(charge)
  const toast = charge.provider_criteria.toast
  const hasDelivery = locked
    ? Boolean(toast?.delivery)
    : charge.criteria_override.handoffs.includes('DELIVERY')
  const hasPickup = locked
    ? Boolean(toast?.takeout)
    : charge.criteria_override.handoffs.includes('PICKUP')
  if (hasDelivery && hasPickup) return { key: 'both', locked }
  if (hasDelivery) return { key: 'delivery', locked }
  if (hasPickup) return { key: 'pickup', locked }
  return { key: 'none', locked }
}

const appliesToText = (
  charge: Pick<ServiceCharge, 'provider_criteria' | 'criteria_override'>
): string => {
  const { key, locked } = describeApplication(charge)
  if (locked) return t('applies_to.toast_controlled')
  switch (key) {
    case 'both':
      return t('applies_to.both')
    case 'delivery':
      return t('applies_to.delivery')
    case 'pickup':
      return t('applies_to.pickup')
    case 'none':
      return t('applies_to.none_yet')
  }
}

const appliesToSentence = (
  charge: Pick<ServiceCharge, 'provider_criteria' | 'criteria_override'>
): string => {
  const { key, locked } = describeApplication(charge)
  switch (key) {
    case 'both':
      return t('applies_to.sentence_both')
    case 'delivery':
      return t('applies_to.sentence_delivery')
    case 'pickup':
      return t('applies_to.sentence_pickup')
    case 'none':
      return locked ? t('applies_to.none_yet') : t('applies_to.not_set')
  }
}

const ControlledInToastChip = ({ label }: { label: string }) => {
  const [css] = useStyletron()
  return (
    <span
      className={`d-inline-flex align-items-center ${css({
        backgroundColor: palette.grey10,
        color: palette.grey70,
        fontSize: '11px',
        fontWeight: 700,
        padding: '3px 9px',
        borderRadius: '4px',
        whiteSpace: 'nowrap',
      })}`}
    >
      <i
        className={`fa fa-lock ${css({
          fontSize: '10px',
          color: palette.grey70,
          marginRight: '6px',
        })}`}
        aria-hidden="true"
      />
      {label}
    </span>
  )
}

type Props = {
  charge: ServiceCharge
  editDisabled: boolean
  editDisabledReason: string | undefined
  onToggleThanxEnabled: (charge: ServiceCharge, next: boolean) => void
  onToggleEligibleForRedemption: (charge: ServiceCharge, next: boolean) => void
  editingId: number | null
  onStartEditing: (charge: ServiceCharge) => void
  lockedIds: Set<number>
  // Only the table (which knows the full list) can say whether this is the
  // last row — the sketch's `.tbl tbody tr:last-child td` drops the border.
  isLastRow?: boolean
}

const ServiceChargeRow = (props: Props) => {
  const { charge, isLastRow = false } = props
  const [css] = useStyletron()

  const rowClassName = tableRowHoverClass(css)
  const cellClassName = tableBodyCellClass(css, isLastRow)

  const isOpen = charge.amount_type === 'OPEN'
  const locked = isOverrideLocked(charge)
  const recommended = isRecommendedForDelivery(charge)
  const showsEmptyState = describeApplication(charge).key === 'none'

  const isEditingThisRow = props.editingId === charge.id
  const controlsDisabled = props.lockedIds.has(charge.id)

  const toggleDisabled = controlsDisabled || props.editDisabled
  const redemptionNeedsActive = !charge.thanx_enabled
  const gratuityBlocksEnabling =
    charge.gratuity && !charge.eligible_for_redemption
  const redemptionDisabled =
    toggleDisabled || redemptionNeedsActive || gratuityBlocksEnabling
  const redemptionReason = (() => {
    if (props.editDisabled) return props.editDisabledReason
    if (gratuityBlocksEnabling) return t('redemption_gratuity_reason')
    if (redemptionNeedsActive) return t('redemption_needs_active_reason')
    return undefined
  })()

  const otherRowEditing = props.editingId !== null && !isEditingThisRow
  const editButtonDisabled = props.editDisabled || otherRowEditing
  const editButtonReason = props.editDisabled
    ? props.editDisabledReason
    : otherRowEditing
    ? t('editing_disabled_reason')
    : undefined

  return (
    <tr
      data-testid={`service-charge-row-${charge.id}`}
      data-service-charge-id={charge.id}
      className={rowClassName}
    >
      <td className={cellClassName}>
        <div className="d-flex align-items-center">
          <Text.BodyText4 bold color="grey90" className="mr-xs">
            {charge.name}
          </Text.BodyText4>
        </div>
        {recommended && (
          <div className="my-xxs">
            <Pill tone="positive">{t('recommended_badge')}</Pill>
          </div>
        )}
        <Text.BodyText5 color="grey60">
          {t('id_label', { uid: charge.provider_uid })}
          {charge.gratuity && ` · ${t('gratuity_tag')}`}
          {isOpen && ` · ${t('open_tag')}`}
        </Text.BodyText5>
      </td>
      <td className={cellClassName}>
        <Text.BodyText4 color="grey90" tag="div">
          {t(`amount_type_labels.${charge.amount_type}`)}
          {!isOpen && ` ${formatServiceChargeAmount(charge)}`}
        </Text.BodyText4>
        <Pill tone="neutral">
          {charge.taxable ? t('taxable') : t('non_taxable')}
        </Pill>
      </td>
      {isOpen ? (
        <td className={cellClassName}>
          <Text.BodyText4
            color={locked || showsEmptyState ? 'grey50' : 'grey90'}
          >
            {locked && (
              <i
                className={`fa fa-lock ${css({
                  fontSize: '11px',
                  marginRight: '6px',
                })}`}
                aria-hidden="true"
              />
            )}
            {appliesToText(charge)}
          </Text.BodyText4>
        </td>
      ) : (
        <td className={cellClassName}>
          <Text.BodyText4
            color={showsEmptyState ? 'grey50' : 'grey90'}
            tag="div"
          >
            {appliesToSentence(charge)}
          </Text.BodyText4>
          {locked && (
            <div className="mt-xxs">
              <ControlledInToastChip label={t('applies_to.toast_controlled')} />
            </div>
          )}
        </td>
      )}
      {isOpen && (
        <>
          <td className={cellClassName} />
          <td className={cellClassName} />
          <td className={cellClassName} />
        </>
      )}
      {!isOpen && (
        <td className={cellClassName}>
          <PermissionTooltip
            disabled={toggleDisabled}
            reason={props.editDisabledReason}
            id={`sc-enabled-${charge.id}`}
          >
            <Toggle
              checked={charge.thanx_enabled}
              disabled={toggleDisabled}
              ariaLabel={t('thanx_enabled_toggle_aria', { name: charge.name })}
              onChange={(next: boolean) =>
                props.onToggleThanxEnabled(charge, next)
              }
            />
          </PermissionTooltip>
        </td>
      )}
      {!isOpen && (
        <td className={cellClassName}>
          <PermissionTooltip
            disabled={redemptionDisabled}
            reason={redemptionReason}
            id={`sc-eligible-for-redemption-${charge.id}`}
          >
            <Toggle
              checked={charge.eligible_for_redemption}
              disabled={redemptionDisabled}
              ariaLabel={t('eligible_for_redemption_toggle_aria', {
                name: charge.name,
              })}
              onChange={(next: boolean) =>
                props.onToggleEligibleForRedemption(charge, next)
              }
            />
          </PermissionTooltip>
        </td>
      )}
      {!isOpen && (
        <td className={cellClassName}>
          {!locked && !isEditingThisRow && (
            <PermissionTooltip
              disabled={editButtonDisabled}
              reason={editButtonReason}
              id={`sc-edit-${charge.id}`}
            >
              <Button
                kind={Kind.MINIMAL}
                size={Size.SMALL}
                onClick={() => props.onStartEditing(charge)}
                disabled={editButtonDisabled}
                startEnhancer={() => (
                  <i className="fa fa-pencil" aria-hidden="true" />
                )}
              >
                {t('edit')}
              </Button>
            </PermissionTooltip>
          )}
        </td>
      )}
    </tr>
  )
}

export default ServiceChargeRow
